import { BattleState, BattleTileState, BattleUnitState, PathStep } from '../types/battle';

// ─── Movement Field v1 (shared spec — keep in sync with BattleMovementService.cs) ───
//
// Constants: COST_SCALE = 1000, THREAT_PENALTY = 1.
// For mover M on battle B:
//   enterable(t): tile exists, isPassable, and no living unit other than M occupies it.
//                 Applies to EVERY entered tile including the destination.
//   stepCost(t)  = max(1, t.movementCost)  (read-time clamp: old battles persisted Road = 0)
//   threatened(t): some living enemy of M is orthogonally adjacent to t.
//   weight(t)    = stepCost(t) * COST_SCALE + (threatened(t) ? THREAT_PENALTY : 0)
// Single-source Dijkstra from S: deterministic scan-min (no heap; grid <= ~400 tiles),
//   pop unsettled node with smallest dist, tie-break smallest (y * gridWidth + x);
//   neighbors in fixed order (+1,0), (-1,0), (0,+1), (0,-1); relax with strict <.
// realCost(t) = floor(dist[t] / COST_SCALE). Exact because the total threat penalty on
//   any path (<= ~100 nodes x 1) can never reach COST_SCALE — this is the invariant
//   proving the threat tie-break never spends extra movement for safety.
// Path = cameFrom walk from goal, reversed, SOURCE EXCLUDED.
// pathCost(steps) = sum of stepCost over entered tiles (revisits pay per entry).

export const COST_SCALE = 1000;
export const THREAT_PENALTY = 1;

export interface Coord {
  x: number;
  y: number;
}

export interface MovementField {
  /** Scaled distance (cost * COST_SCALE + threat penalties) keyed by "x,y". */
  dist: Map<string, number>;
  /** Predecessor tile keyed by "x,y". */
  cameFrom: Map<string, string>;
}

const key = (x: number, y: number) => `${x},${y}`;

function tileAt(battle: BattleState, x: number, y: number): BattleTileState | undefined {
  return battle.tiles.find(t => t.x === x && t.y === y);
}

function livingEnemiesOf(battle: BattleState, mover: BattleUnitState): BattleUnitState[] {
  return battle.units.filter(u => u.isPlayerUnit !== mover.isPlayerUnit && u.stats.currentHP > 0);
}

function isEnterable(battle: BattleState, mover: BattleUnitState, x: number, y: number): boolean {
  const tile = tileAt(battle, x, y);
  if (!tile || !tile.isPassable) return false;
  return !battle.units.some(u =>
    u.id !== mover.id && u.position.x === x && u.position.y === y && u.stats.currentHP > 0);
}

function stepCost(tile: BattleTileState): number {
  return Math.max(1, tile.movementCost);
}

function computeThreatenedTiles(battle: BattleState, mover: BattleUnitState): Set<string> {
  const threatened = new Set<string>();
  for (const enemy of livingEnemiesOf(battle, mover)) {
    threatened.add(key(enemy.position.x + 1, enemy.position.y));
    threatened.add(key(enemy.position.x - 1, enemy.position.y));
    threatened.add(key(enemy.position.x, enemy.position.y + 1));
    threatened.add(key(enemy.position.x, enemy.position.y - 1));
  }
  return threatened;
}

/**
 * Single-source Dijkstra per Movement Field v1 (see spec comment above).
 * Mirrors BattleMovementService.ComputeMovementField exactly.
 */
export function computeMovementField(
  battle: BattleState,
  mover: BattleUnitState,
  start: Coord
): MovementField {
  const dist = new Map<string, number>([[key(start.x, start.y), 0]]);
  const cameFrom = new Map<string, string>();
  const settled = new Set<string>();
  const threatened = computeThreatenedTiles(battle, mover);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Deterministic scan-min: smallest dist, tie-break smallest y * gridWidth + x.
    let current: string | null = null;
    let best = Infinity;
    let bestOrder = Infinity;
    dist.forEach((d, pos) => {
      if (settled.has(pos)) return;
      const [px, py] = pos.split(',').map(Number);
      const order = py * battle.gridWidth + px;
      if (d < best || (d === best && order < bestOrder)) {
        best = d;
        bestOrder = order;
        current = pos;
      }
    });

    if (current === null) break;
    const cur: string = current;
    settled.add(cur);
    const [cx, cy] = cur.split(',').map(Number);

    const neighbors: Coord[] = [
      { x: cx + 1, y: cy },
      { x: cx - 1, y: cy },
      { x: cx, y: cy + 1 },
      { x: cx, y: cy - 1 },
    ];

    for (const { x: nx, y: ny } of neighbors) {
      if (nx < 0 || ny < 0 || nx >= battle.gridWidth || ny >= battle.gridHeight) continue;
      const nk = key(nx, ny);
      if (settled.has(nk)) continue;
      if (!isEnterable(battle, mover, nx, ny)) continue;

      const tile = tileAt(battle, nx, ny)!;
      const weight = stepCost(tile) * COST_SCALE + (threatened.has(nk) ? THREAT_PENALTY : 0);
      const nd = dist.get(cur)! + weight;

      const existing = dist.get(nk);
      if (existing === undefined || nd < existing) {
        dist.set(nk, nd);
        cameFrom.set(nk, cur);
      }
    }
  }

  return { dist, cameFrom };
}

/**
 * Cost-optimal, threat-tiebreak path from `from` to `to` (entered tiles in order,
 * source excluded). Null when unreachable.
 */
export function findSegmentPath(
  battle: BattleState,
  mover: BattleUnitState,
  from: Coord,
  to: Coord
): Coord[] | null {
  if (from.x === to.x && from.y === to.y) return [];

  const { dist, cameFrom } = computeMovementField(battle, mover, from);
  if (!dist.has(key(to.x, to.y))) return null;

  const path: Coord[] = [];
  let cursor = key(to.x, to.y);
  while (cameFrom.has(cursor)) {
    const [x, y] = cursor.split(',').map(Number);
    path.push({ x, y });
    cursor = cameFrom.get(cursor)!;
  }
  path.reverse();
  return path;
}

/**
 * Routes mover -> waypoint1 -> ... -> dest, concatenating cost-optimal segments.
 * Revisited tiles pay again on each entry. Null when any segment is unreachable.
 */
export function routeThroughWaypoints(
  battle: BattleState,
  mover: BattleUnitState,
  waypoints: Coord[],
  dest: Coord
): { steps: Coord[]; cost: number } | null {
  const steps: Coord[] = [];
  let from: Coord = { x: mover.position.x, y: mover.position.y };

  for (const target of [...waypoints, dest]) {
    const segment = findSegmentPath(battle, mover, from, target);
    if (segment === null) return null;
    steps.push(...segment);
    from = target;
  }

  if (steps.length === 0) return null;

  let cost = 0;
  for (const step of steps) {
    const tile = tileAt(battle, step.x, step.y);
    if (!tile) return null;
    cost += stepCost(tile);
  }

  return { steps, cost };
}

function isAdjacent(x1: number, y1: number, x2: number, y2: number): boolean {
  const dx = Math.abs(x1 - x2);
  const dy = Math.abs(y1 - y2);
  return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
}

/**
 * Annotates a routed path with cumulative cost and opportunity-attack warnings.
 * OA rule mirrors OpportunityAttackService: an enemy triggers (once per move) on the
 * step where the mover was orthogonally adjacent and stops being adjacent.
 */
export function annotatePath(
  battle: BattleState,
  mover: BattleUnitState,
  steps: Coord[]
): PathStep[] {
  const enemies = livingEnemiesOf(battle, mover);
  const triggered = new Set<number>();
  const fullPath: Coord[] = [{ x: mover.position.x, y: mover.position.y }, ...steps];

  const annotated: PathStep[] = [];
  let cumulativeCost = 0;

  for (let i = 1; i < fullPath.length; i++) {
    const prev = fullPath[i - 1];
    const step = fullPath[i];
    const tile = tileAt(battle, step.x, step.y);
    cumulativeCost += tile ? stepCost(tile) : 1;

    let provokesOA = false;
    for (const enemy of enemies) {
      if (triggered.has(enemy.id)) continue;
      if (isAdjacent(enemy.position.x, enemy.position.y, prev.x, prev.y) &&
          !isAdjacent(enemy.position.x, enemy.position.y, step.x, step.y)) {
        triggered.add(enemy.id);
        provokesOA = true;
      }
    }

    annotated.push({ x: step.x, y: step.y, cumulativeCost, provokesOA });
  }

  return annotated;
}
